import type { DataTableColumn } from "@etudescarre/ui";
import { isNotNil } from "@etudescarre/utils";
import { DownloadSimple as DownloadIcon } from "@phosphor-icons/react";
import { useEffect, useMemo } from "react";
import { useParams } from "react-router";
import { apiClient } from "@/lib/api/client";
import { formatDay } from "@/lib/utils/date";
import { Classeur, type FilterDef } from "../components/classeur";
import { downloadReportResponse } from "../components/classeur/download-report-response";
import {
  useClasseurFilters,
  useClasseurState,
} from "../components/classeur/use-classeur-data";

type ReportResponseItem = {
  id: string;
  dateReponse: string | null;
  authorEmployee: string | null;
  reports: { numero: string | null; contractName: string | null }[];
};

const EMETTEUR_FILTER_KEY = "emetteur";

export function ClasseurReponsesRapports() {
  const { yardId = "" } = useParams<{ yardId: string }>();
  const [state, setState] = useClasseurState<ReportResponseItem>();

  const columns = useMemo<DataTableColumn<ReportResponseItem>[]>(
    () => [
      { id: "id", header: "N°", accessor: "id" },
      {
        id: "dateReponse",
        header: "Date réponse",
        accessor: "dateReponse",
        cell: ({ value }) => (value ? formatDay(value as string) : "-"),
      },
      { id: "authorEmployee", header: "Auteur", accessor: "authorEmployee" },
      {
        id: "reports",
        header: "Rapports concernés",
        accessor: (row) =>
          row.reports
            .map(({ numero, contractName }) =>
              [numero, contractName && `(${contractName})`]
                .filter(Boolean)
                .join(" "),
            )
            .join(", "),
      },
      {
        id: "download",
        header: "Réponse",
        accessor: "id",
        cell: ({ row }) => (
          <button
            type="button"
            aria-label={`Télécharger la réponse ${row.id}`}
            title={`Télécharger la réponse ${row.id}`}
            className="text-blue-600 hover:text-blue-700"
            onClick={() => downloadReportResponse(yardId, Number(row.id))}
          >
            <DownloadIcon size={18} />
          </button>
        ),
      },
    ],
    [yardId],
  );

  const filters = useMemo<FilterDef[]>(
    () => [
      {
        key: EMETTEUR_FILTER_KEY,
        label: "Contrat auteur du rapport",
        options: [
          ...new Set(
            state.items
              .flatMap((item) => item.reports.map((r) => r.contractName))
              .filter(isNotNil),
          ),
        ].map((name) => ({ value: name, label: name })),
      },
    ],
    [state.items],
  );
  const { activeFilters, onFilterChange } = useClasseurFilters({ filters });
  const emetteur = activeFilters[EMETTEUR_FILTER_KEY]?.[0];
  const items = state.items.filter(
    (item) =>
      !isNotNil(emetteur) ||
      item.reports.some((report) => report.contractName === emetteur),
  );

  useEffect(() => {
    async function fetchData() {
      setState((prev) => ({ ...prev, isLoading: true }));
      const { data } = await apiClient.GET(
        "/api/yards/{yardId}/report-responses",
        {
          params: { path: { yardId } },
        },
      );
      if (data) {
        const items = data.data.items.map((item) => ({
          ...item,
          id: String(item.id),
        }));
        setState({ items, total: data.data.total, isLoading: false });
      } else {
        setState({ items: [], total: 0, isLoading: false });
      }
    }
    fetchData();
  }, [yardId, setState]);

  return (
    <Classeur
      title="Classeur réponses à rapports"
      items={items}
      columns={columns}
      filters={filters}
      activeFilters={activeFilters}
      onFilterChange={onFilterChange}
      isLoading={state.isLoading}
      emptyMessage="Aucune réponse à rapport"
      total={items.length}
    />
  );
}
